import { useState } from "react";

import { downloadCalculationWorkbook, type WorkbookVariant } from "@/api";
import { Button } from "@/components/Button";

import styles from "./CalculationScreen.module.css";
import type { QuoteRelease } from "./readiness";

/** Hands a downloaded file to the browser under the server's name. */
function save(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

interface Wording {
  label: string;
  busy: string;
  title: string;
  failed: string;
}

const WORDING: Record<WorkbookVariant, Wording> = {
  angebot: {
    label: "Angebot (Excel)",
    busy: "Angebot wird erstellt …",
    title:
      "Lädt das Angebot als Excel: Positionen, Lieferant je Position, Verkaufspreise und Lücken — ohne Einkauf und Marge. Das darfst du weitergeben.",
    failed: "Das Angebot ließ sich nicht als Excel erstellen. Versuch es gleich noch einmal.",
  },
  intern: {
    label: "Kalkulation intern (Excel)",
    busy: "Kalkulation wird erstellt …",
    title:
      "Lädt die interne Kalkulation als Excel: Einkauf, Marge, Lieferantenvergleich und Preisstand. Die gibst du nicht weiter.",
    failed: "Die interne Kalkulation ließ sich nicht als Excel erstellen. Versuch es gleich noch einmal.",
  },
};

/** The quote before its release (E-52): the server marks the file „ENTWURF —
 * nicht freigegeben" and names it `Entwurf_…`; the button says the same. */
const DRAFT: Omit<Wording, "title"> = {
  label: "Angebotsentwurf (Excel)",
  busy: "Angebotsentwurf wird erstellt …",
  failed: "Der Angebotsentwurf ließ sich nicht als Excel erstellen. Versuch es gleich noch einmal.",
};

/** Why it is a draft, as the file's status line says it (Task 13d,
 * Zweitgutachten N4) — a release that no longer holds is not „noch nicht
 * freigegeben". */
const DRAFT_TITLE: Record<Exclude<QuoteRelease, "holds">, string> = {
  none: "Noch nicht freigegeben — die Datei ist als Entwurf gekennzeichnet und nicht für den Kunden.",
  withdrawn: "Die Freigabe ist zurückgezogen — die Datei ist als Entwurf gekennzeichnet und nicht für den Kunden.",
  superseded: "Die Freigabe gilt nicht mehr — die Datei ist als Entwurf gekennzeichnet und nicht für den Kunden.",
};

/**
 * One of the calculation's two workbooks (N-20, Entscheid Denis 23.09.2026):
 * „Angebot (Excel)" may leave the house, „Kalkulation intern (Excel)" carries
 * purchase prices and margins and does not. Two files, so forwarding the
 * quote can never forward the internal sheet along with it. Reads only; open
 * on a released and on a frozen Vorgang alike, because taking the figures to
 * JTL is exactly what happens after the release.
 *
 * **Before the release the quote is a draft** (`release`, E-52, UX-Gutachten
 * 23.10.2026 Nr. 3): still downloadable — a person may want to look at the
 * figures in Excel — but „Angebotsentwurf (Excel)", with the tooltip saying
 * why and that it is not for the customer. The file says it too; the server decides that, from
 * the same release and staleness, so a stale screen cannot hand out a clean
 * file. The internal calculation has no draft: it never leaves the house.
 *
 * A failure stays next to the button in words (`role="alert"`), a success
 * goes to the screen's live region through `onDone`.
 */
export function ExportButton({
  caseId,
  calcId,
  variant,
  release = "holds",
  onDone,
}: {
  caseId: string;
  calcId: string;
  variant: WorkbookVariant;
  /** Whether the shown calculation's release holds (`quoteRelease`). */
  release?: QuoteRelease;
  onDone: (line: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const draftTitle = variant === "angebot" && release !== "holds" ? DRAFT_TITLE[release] : null;
  const draft = draftTitle !== null;
  const words = draft ? { ...DRAFT, title: draftTitle } : WORDING[variant];

  async function download(): Promise<void> {
    setBusy(true);
    setFailed(false);
    try {
      const { blob, filename } = await downloadCalculationWorkbook(caseId, calcId, variant, { draft });
      save(blob, filename);
      onDone(`Excel-Datei ${filename} geladen.`);
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Button onClick={() => void download()} disabled={busy} title={words.title}>
        {busy ? words.busy : words.label}
      </Button>
      {failed ? (
        <p role="alert" className={`${styles.note} ${styles.noteBad}`}>
          {words.failed}
        </p>
      ) : null}
    </>
  );
}
